import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Bell, 
  X, 
  CheckCircle, 
  AlertTriangle, 
  Info, 
  Clock,
  Settings
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useNotifications } from '@/context/NotificationContext'
import { formatDate, getDaysRemaining } from '@/lib/utils'

interface SystemNotification {
  id: string
  type: 'success' | 'warning' | 'error' | 'info'
  title: string
  message: string
  timestamp: Date
  read: boolean
  action?: {
    label: string
    onClick: () => void
  }
}

export default function NotificationCenter() {
  const { user } = useAuth()
  const { isSupported, isSubscribed, permission, sendTestNotification } = useNotifications()
  const [notifications, setNotifications] = useState<SystemNotification[]>([])
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    generateSystemNotifications()
  }, [user])

  const generateSystemNotifications = () => {
    if (!user) return

    const systemNotifications: SystemNotification[] = []

    // Push notification status
    if (isSupported) {
      if (!isSubscribed) {
        systemNotifications.push({
          id: 'push-notifications',
          type: 'info',
          title: 'Enable Push Notifications',
          message: 'Stay updated with real-time notifications about your restaurant\'s performance and important alerts.',
          timestamp: new Date(),
          read: false,
          action: {
            label: 'Enable Now',
            onClick: async () => {
              // This will be handled by the notification context
              console.log('Enable push notifications')
            }
          }
        })
      } else if (permission === 'granted') {
        systemNotifications.push({
          id: 'push-enabled',
          type: 'success',
          title: 'Push Notifications Enabled',
          message: 'You\'re all set to receive important notifications.',
          timestamp: new Date(),
          read: true
        })
      }
    }

    // Subscription-related notifications for restaurant owners
    if (user.userType === 'restaurant_owner') {
      if (user.subscriptionEndDate) {
        const daysRemaining = getDaysRemaining(new Date(user.subscriptionEndDate))
        
        if (daysRemaining <= 30 && daysRemaining > 0) {
          systemNotifications.push({
            id: 'subscription-expiry',
            type: daysRemaining <= 7 ? 'error' : 'warning',
            title: 'Subscription Expiring Soon',
            message: `Your subscription expires in ${daysRemaining} days on ${formatDate(user.subscriptionEndDate)}. Contact admin to renew.`,
            timestamp: new Date(),
            read: false,
            action: {
              label: 'Contact Admin',
              onClick: () => {
                // Navigate to contact or support
                console.log('Contact admin for renewal')
              }
            }
          })
        } else if (daysRemaining <= 0) {
          systemNotifications.push({
            id: 'subscription-expired',
            type: 'error',
            title: 'Subscription Expired',
            message: 'Your subscription has expired. Please contact admin to renew your access.',
            timestamp: new Date(),
            read: false,
            action: {
              label: 'Contact Admin',
              onClick: () => {
                console.log('Contact admin for renewal')
              }
            }
          })
        }
      }

      // Database connection status
      if (user.databaseName) {
        systemNotifications.push({
          id: 'database-connected',
          type: 'success',
          title: 'Database Connected',
          message: `Connected to database: ${user.databaseName}`,
          timestamp: new Date(),
          read: true
        })
      }
    }

    // Admin notifications
    if (user.userType === 'admin') {
      systemNotifications.push({
        id: 'admin-access',
        type: 'info',
        title: 'Admin Dashboard Access',
        message: 'You have full administrative access to manage restaurants, subscriptions, and users.',
        timestamp: new Date(),
        read: true
      })

      // System health notification
      systemNotifications.push({
        id: 'system-health',
        type: 'success',
        title: 'All Systems Operational',
        message: 'All restaurant databases and services are running normally.',
        timestamp: new Date(),
        read: true
      })
    }

    setNotifications(systemNotifications)
  }

  const markAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, read: true }
          : notification
      )
    )
  }

  const dismissNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id))
  }

  const getIcon = (type: SystemNotification['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-orange-600" />
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-600" />
      case 'info':
      default:
        return <Info className="h-4 w-4 text-blue-600" />
    }
  }

  const getAlertVariant = (type: SystemNotification['type']) => {
    switch (type) {
      case 'error':
        return 'destructive' as const
      case 'warning':
        return 'default' as const
      default:
        return 'default' as const
    }
  }

  const unreadCount = notifications.filter(n => !n.read).length
  const displayNotifications = showAll ? notifications : notifications.slice(0, 3)

  if (notifications.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-muted-foreground">
            <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No notifications at this time</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notifications
            {unreadCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {unreadCount}
              </Badge>
            )}
          </CardTitle>
          
          <div className="flex items-center gap-2">
            {isSubscribed && (
              <Button
                variant="ghost"
                size="sm"
                onClick={sendTestNotification}
                className="text-xs"
              >
                Test Push
              </Button>
            )}
            
            {notifications.length > 3 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAll(!showAll)}
                className="text-xs"
              >
                {showAll ? 'Show Less' : `Show All (${notifications.length})`}
              </Button>
            )}
          </div>
        </div>
        
        <CardDescription>
          System notifications and important updates
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-3">
          {displayNotifications.map((notification) => (
            <Alert 
              key={notification.id} 
              variant={getAlertVariant(notification.type)}
              className={`relative ${!notification.read ? 'border-l-4 border-l-blue-500' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  {getIcon(notification.type)}
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-sm">{notification.title}</h4>
                      {!notification.read && (
                        <Badge variant="secondary" className="text-xs">New</Badge>
                      )}
                    </div>
                    
                    <AlertDescription className="text-sm">
                      {notification.message}
                    </AlertDescription>
                    
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatDate(notification.timestamp)}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {notification.action && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              notification.action!.onClick()
                              markAsRead(notification.id)
                            }}
                            className="text-xs"
                          >
                            {notification.action.label}
                          </Button>
                        )}
                        
                        {!notification.read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => markAsRead(notification.id)}
                            className="text-xs"
                          >
                            Mark Read
                          </Button>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => dismissNotification(notification.id)}
                          className="text-xs h-6 w-6 p-0"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Alert>
          ))}
        </div>
        
        {/* Push notification setup reminder */}
        {isSupported && !isSubscribed && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-2">
              <Settings className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-blue-900 dark:text-blue-100">
                Enable Push Notifications
              </span>
            </div>
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
              Get instant notifications for subscription alerts, system updates, and important events.
            </p>
            <Button 
              size="sm" 
              onClick={() => {
                // This should trigger the notification context to request permission
                console.log('Setup push notifications')
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Enable Notifications
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}